import getWindow from 'get-window';

import type { Change } from '@gitbook/slate';
import { IS_FIREFOX, IS_IE, IS_IOS, IS_SAFARI } from '@gitbook/slate-dev-environment';
import Hotkeys from '@gitbook/slate-hotkeys';
import { Logger } from '@gitbook/util-debug';

import type { EditorContainer } from '../interfaces';
import { findNode, findRange } from '../utils';
import type { Plugin } from './plugin';

const logger = Logger('slate:before');

/*
 * In edit mode, uses the current change selection. In read mode,
 * tries to compute the current Slate range from the native
 * selection, and returns the updated change.
 * Returns null if no selection could be computed.
 */

function ensureSlateSelection(event: Event, change: Change, editor: EditorContainer) {
    const { readOnly } = editor;
    const { value } = change;

    if (!readOnly) {
        return true;
    }

    // We need to compute the current selection
    const window = getWindow(event.target);
    const native = window.getSelection();
    const range = findRange(native, value);

    if (!range) {
        // We don't have a Slate selection
        return false;
    }

    // Ensure the value has the correct selection set
    change.select(range);
    return true;
}

/*
 * The core before plugin.
 */
function BeforePlugin(): Plugin {
    let activeElement = null;
    let compositionCount = 0;
    let isCopying = false;
    let isDragging = false;

    /*
     * On blur.
     */

    function onBlur(event: Event, change: Change, editor: EditorContainer) {
        if (isCopying) {
            return true;
        }
        if (editor.readOnly) {
            return true;
        }

        const { value } = change;
        // @ts-expect-error
        const { relatedTarget, target } = event;
        const window = getWindow(target);

        // COMPAT: If the current `activeElement` is still the previous one, this is
        // due to the window being blurred when the tab itself becomes unfocused, so
        // we want to abort early to allow to editor to stay focused when the tab
        // becomes focused again.
        if (activeElement === window.document.activeElement) {
            return true;
        }

        // COMPAT: The `relatedTarget` can be null when the new focus target is not
        // a "focusable" element (eg. a `<div>` without `tabindex` set).
        if (relatedTarget) {
            const el = editor.elementRef?.current;

            // COMPAT: The event should be ignored if the focus is returning to the
            // editor from an embedded editable element (eg. an <input> element inside
            // a void node).
            if (relatedTarget === el) {
                return true;
            }

            // COMPAT: The event should be ignored if the focus is moving from the
            // editor to inside a void node's spacer element.
            if (relatedTarget.hasAttribute('data-slate-spacer')) {
                return true;
            }

            if (el?.contains(relatedTarget)) {
                // COMPAT: The event should be ignored if the focus is moving to a non-
                // editable section of an element that isn't a void node (eg. a list item
                // of the check list example).
                const node = findNode(relatedTarget, value);
                // @ts-expect-error
                if (node && !node.isVoid) {
                    return true;
                }
            }
        }

        logger.debug('onBlur', { event });
    }

    /*
     * On composition end.
     */

    function onCompositionEnd(event: InputEvent, change: Change, editor: EditorContainer) {
        const n = compositionCount;

        // COMPAT: In Chrome, `beforeinput` events for compositions
        // aren't correct and never fire the "insertFromComposition"
        // type that we need. So instead, insert whenever a composition
        // ends since it will already have been committed to the DOM.
        // https://bugs.chromium.org/p/chromium/issues/detail?id=1022204
        if (!IS_SAFARI && !IS_IOS && event.data) {
            change.insertText(event.data);
        }

        // The `count` check here ensures that if another composition starts
        // before the timeout has closed out this one, we will abort unsetting the
        // `isComposing` flag, since a composition is still in affect.
        window.requestAnimationFrame(() => {
            if (compositionCount > n) {
                return;
            }
            editor.isComposing = false;
        });

        logger.debug('onCompositionEnd', { event });
    }

    /*
     * On composition start.
     */

    function onCompositionStart(event: Event, change: Change, editor: EditorContainer) {
        editor.isComposing = true;
        compositionCount++;

        logger.debug('onCompositionStart', { event });

        const { selection } = change.value;
        if (selection.isExpanded) {
            change.delete();
            return;
        }
    }

    /*
     * On composition update.
     */

    function onCompositionUpdate(event: Event, _change: Change, editor: EditorContainer) {
        logger.debug('onCompositionUpdate', { event });
        editor.isComposing = true;
    }

    /*
     * On copy.
     */

    function onCopy(event: Event, change: Change, editor: EditorContainer) {
        const window = getWindow(event.target);

        const hasSelection = ensureSlateSelection(event, change, editor);

        if (!hasSelection) {
            // We don't have a selection, so let the browser
            // handle the copy, and do not call other plugins
            return true;
        }

        isCopying = true;
        window.requestAnimationFrame(() => (isCopying = false));

        logger.debug('onCopy', { event });
    }

    /*
     * On cut.
     */

    function onCut(event: Event) {
        const window = getWindow(event.target);
        isCopying = true;
        window.requestAnimationFrame(() => (isCopying = false));

        logger.debug('onCut', { event });
    }

    /*
     * On drag end.
     */

    function onDragEnd(event: Event) {
        isDragging = false;

        logger.debug('onDragEnd', { event });
    }

    /*
     * On drag enter.
     */

    function onDragEnter(event: Event) {
        logger.debug('onDragEnter', { event });
    }

    /*
     * On drag exit.
     */

    function onDragExit(event: Event) {
        logger.debug('onDragExit', { event });
    }

    /*
     * On drag leave.
     */

    function onDragLeave(event: Event) {
        logger.debug('onDragLeave', { event });
    }

    /*
     * On drag over.
     */

    function onDragOver(event: Event, _change: Change, editor: EditorContainer) {
        // If the target is inside a void node, and only in this case,
        // call `preventDefault` to signal that drops are allowed.
        // When the target is editable, dropping is already allowed by
        // default, and calling `preventDefault` hides the cursor.
        // @ts-expect-error
        const node = findNode(event.target, editor.value);
        // @ts-expect-error
        if (node.isVoid) {
            event.preventDefault();
        }

        // COMPAT: IE won't call onDrop on contentEditables unless the
        // default dragOver is prevented:
        // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/913982/
        // (2018/07/11)
        if (IS_IE) {
            event.preventDefault();
        }

        // If a drag is already in progress, don't do this again.
        if (!isDragging) {
            isDragging = true;

            // COMPAT: IE will raise an `unspecified error` if dropEffect is
            // set. (2018/07/11)
            if (!IS_IE) {
                // @ts-expect-error
                event.nativeEvent.dataTransfer.dropEffect = 'move';
            }
        }

        logger.debug('onDragOver', { event });
    }

    /*
     * On drag start.
     */

    function onDragStart(event: Event) {
        isDragging = true;

        logger.debug('onDragStart', { event });
    }

    /*
     * On drop.
     */

    function onDrop(event: Event, _change: Change, editor: EditorContainer) {
        // Nothing happens in read-only mode.
        if (editor.readOnly) {
            return true;
        }

        // Prevent default so the DOM's value isn't corrupted.
        event.preventDefault();

        logger.debug('onDrop', { event });
    }

    /*
     * On focus.
     */

    function onFocus(event: Event, _change: Change, editor: EditorContainer) {
        if (isCopying) {
            return true;
        }

        if (editor.readOnly) {
            return true;
        }

        const el = editor.elementRef?.current;

        // Save the new `activeElement`.
        const window = getWindow(event.target);
        activeElement = window.document.activeElement;

        // COMPAT: If the editor has nested editable elements, the focus can go to
        // those elements. In Firefox, this must be prevented because it results in
        // issues with keyboard navigation. (2017/03/30)
        if (IS_FIREFOX && el && event.target !== el) {
            editor.isUpdatingSelection.current = true;
            el.focus({ preventScroll: true });
            editor.isUpdatingSelection.current = false;
            return true;
        }

        logger.debug('onFocus', { event });
    }

    /*
     * On input.
     */

    function onInput(event: Event, change: Change, editor: EditorContainer) {
        if (editor.isComposing) {
            return true;
        }
        if (change.value.isBlurred) {
            return true;
        }

        logger.debug('onInput', { event });
    }

    /*
     * On key down.
     */

    function onKeyDown(event: Event, _change: Change, editor: EditorContainer) {
        if (editor.readOnly) {
            return true;
        }

        // When composing, we need to prevent all hotkeys from executing while
        // typing. However, certain characters also move the selection before
        // we're able to handle it, so prevent their default behavior.
        if (editor.isComposing) {
            // @ts-expect-error
            if (Hotkeys.isComposing(event)) {
                event.preventDefault();
            }
            return true;
        }

        // Certain hotkeys have native behavior in contenteditable elements which
        // will cause our value to be out of sync, so prevent them.
        // @ts-expect-error
        if (Hotkeys.isContentEditable(event) && !IS_IOS) {
            event.preventDefault();
        }

        logger.debug('onKeyDown', { event });
    }

    /*
     * On paste.
     */

    function onPaste(event: Event, _change: Change, editor: EditorContainer) {
        if (editor.readOnly) {
            return true;
        }

        // Prevent defaults so the DOM state isn't corrupted.
        event.preventDefault();

        logger.debug('onPaste', { event });
    }

    /*
     * On select.
     */

    function onSelect(event: Event, _change: Change, editor: EditorContainer) {
        if (isCopying) {
            return true;
        }
        if (editor.isComposing) {
            return true;
        }
        if (editor.readOnly) {
            return true;
        }

        // Save the new `activeElement`.
        const window = getWindow(event.target);
        activeElement = window.document.activeElement;

        logger.debug('onSelect', { event });
    }

    return {
        onBlur,
        onCompositionEnd,
        onCompositionUpdate,
        onCompositionStart,
        onCopy,
        onCut,
        onDragEnd,
        onDragEnter,
        onDragExit,
        onDragLeave,
        onDragOver,
        onDragStart,
        onDrop,
        onFocus,
        onInput,
        onKeyDown,
        onPaste,
        onSelect,
    };
}

export default BeforePlugin;
